import { useState, useCallback, useEffect } from 'react';
import type { DependencyType } from '../types';

export type ConnectionPoint = {
  taskId: string;
  side: 'start' | 'end';
};

type ActiveDrag = ConnectionPoint & {
  currentX: number;
  currentY: number;
};

type UseDependencyDragArgs = {
  addDependency: (from: string, to: string, type: DependencyType) => void;
  ganttRef: React.RefObject<HTMLDivElement>; // ссылка на контейнер для координат
};

export const useDependencyDrag = ({ addDependency, ganttRef }: UseDependencyDragArgs) => {
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);

  // Обработка клика по точке
  const handlePointClick = useCallback(
    (taskId: string, side: 'start' | 'end', clientX: number, clientY: number) => {
      // Если уже тянем линию — это завершение
      if (activeDrag) {
        // Нельзя соединить задачу саму с собой (пока ограничим)
        if (activeDrag.taskId === taskId) {
          setActiveDrag(null);
          return;
        }

        // Определяем тип связи: fromSide -> toSide
        let type: DependencyType = 'FS'; // default
        if (activeDrag.side === 'start' && side === 'start') type = 'SS';
        if (activeDrag.side === 'start' && side === 'end') type = 'SF';
        if (activeDrag.side === 'end' && side === 'start') type = 'FS';
        if (activeDrag.side === 'end' && side === 'end') type = 'FF';

        addDependency(activeDrag.taskId, taskId, type);
        setActiveDrag(null);
      } else {
        // Начало новой связи
        // Вычисляем относительные координаты внутри GanttGrid
        if (!ganttRef.current) return;
        const rect = ganttRef.current.getBoundingClientRect();
        
        setActiveDrag({
          taskId,
          side,
          currentX: clientX - rect.left,
          currentY: clientY - rect.top,
        });
      }
    },
    [activeDrag, addDependency, ganttRef]
  );

  // Глобальный mousemove для "призрачной" линии
  useEffect(() => {
    if (!activeDrag) return;

    const onMove = (e: MouseEvent) => {
      if (!ganttRef.current) return;
      const rect = ganttRef.current.getBoundingClientRect();
      setActiveDrag(prev =>
        prev
          ? {
              ...prev,
              currentX: e.clientX - rect.left,
              currentY: e.clientY - rect.top,
            }
          : null
      );
    };

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveDrag(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onEsc);
    };
  }, [activeDrag, ganttRef]);

  return {
    activeDrag,
    handlePointClick,
    cancelDrag: () => setActiveDrag(null),
  };
};
